/**
 * Browser refresh: **POST** only — never navigate the browser to `/auth/` as a page.
 *
 * Allowed refresh paths (via {@link resolveAuthRefreshApiPathTail}):
 * - `auth/refresh`
 * - `refreshtokens`
 * - `refreshtokens/refresh` (default; matches nginx on ofizz hosts)
 *
 * Uses **`credentials: 'include'`** so the httpOnly **`refreshToken`** cookie (set at login) is sent.
 * Falls back to JSON **`{ refreshToken }`** from storage / **`public/refreshtoken.txt`** when cookie refresh fails.
 *
 * Refresh token resolution for the JSON body fallback (when **`REACT_APP_AUTH_REFRESH_DISABLED`** is not **`true`**):
 * 1. **`REACT_APP_REFRESH_TOKEN`**
 * 2. **`localStorage`** `ofrontend.refreshToken` — from login JSON when the API returns it in the body
 * 3. First non-comment line of **`public/refreshtoken.txt`**
 */
import { getStoredRefreshToken, persistStoredRefreshToken } from '../auth/sessionToken';
import { publicPath } from '../publicPath';
import { isSuccessfulHttpStatus } from './apiFetch';
import { extractAccessTokenFromLoginJson } from './authLoginResponse';
import { fetchWithDevProxyFallback, type DevProxyRoute } from './fetchWithDevProxyFallback';
import { isUserInitiatedLogoutGateActive } from '../auth/logoutGate';

const REFRESH_UNAVAILABLE_KEY = 'ofrontend.refreshUnavailable404';

/** Clears “refresh endpoint missing” memo for this tab (also cleared after a successful refresh). */
export function clearRefreshUnavailable404(): void {
  try {
    sessionStorage.removeItem(REFRESH_UNAVAILABLE_KEY);
  } catch {
    /* private mode */
  }
}

function rememberRefreshUnavailable404(): void {
  try {
    sessionStorage.setItem(REFRESH_UNAVAILABLE_KEY, '1');
  } catch {
    /* private mode */
  }
}

function isRefreshUnavailable404Cached(): boolean {
  try {
    return sessionStorage.getItem(REFRESH_UNAVAILABLE_KEY) === '1';
  } catch {
    return false;
  }
}

/** Non-OK refresh HTTP response — used to detect 404 and stop spamming `/auth/refresh`. */
export class AuthRefreshHttpError extends Error {
  readonly httpStatus: number;

  constructor(httpStatus: number, message: string) {
    super(message);
    this.name = 'AuthRefreshHttpError';
    this.httpStatus = httpStatus;
  }
}

let refreshPromise: Promise<string | null> | null = null;

export function resetRefreshTokenCaches(): void {
  refreshPromise = null;
  clearRefreshUnavailable404();
}

/** Pull refresh string from login / refresh JSON bodies (`refreshToken`, `refresh_token`, …). */
export function extractRefreshTokenFromUnknown(body: unknown): string | null {
  if (!body || typeof body !== 'object') {
    return null;
  }
  const o = body as Record<string, unknown>;
  const candidates = [o.refreshToken, o.refresh_token, o.RefreshToken];
  for (const c of candidates) {
    if (typeof c === 'string' && c.trim()) {
      return c.trim();
    }
  }
  return null;
}

/** Same as Node reading `refreshtoken.txt` each time — no stale in-memory cache. */
async function loadRefreshTokenFromPublicFile(): Promise<string | null> {
  try {
    const res = await fetch(publicPath('/refreshtoken.txt'), { cache: 'no-store' });
    if (!res.ok) {
      return null;
    }
    const raw = (await res.text()).trim().replace(/^\uFEFF/, '');
    const line =
      raw
        .split(/\r?\n/)
        .map((l) => l.trim())
        .find((l) => l.length > 0 && !l.startsWith('#')) ?? null;
    return line || null;
  } catch {
    return null;
  }
}

export async function getBrowserRefreshToken(): Promise<string | null> {
  if (process.env.REACT_APP_AUTH_REFRESH_DISABLED === 'true') {
    return null;
  }
  const fromEnv = process.env.REACT_APP_REFRESH_TOKEN?.trim();
  if (fromEnv) {
    return fromEnv;
  }
  if (isRefreshUnavailable404Cached()) {
    return null;
  }
  const fromSession = getStoredRefreshToken();
  if (fromSession) {
    return fromSession;
  }
  return loadRefreshTokenFromPublicFile();
}

/**
 * Dev proxy for auth refresh POST.
 * When **`REACT_APP_LOGIN_PROXY_TARGET`** is set, refresh uses **`/dev-login-proxy`** (same auth service).
 * Override with **`REACT_APP_AUTH_REFRESH_DEV_PROXY=api`** to keep refresh on the main API host.
 */
export function resolveAuthRefreshDevProxyRoute(): DevProxyRoute {
  const raw = process.env.REACT_APP_AUTH_REFRESH_DEV_PROXY?.trim().toLowerCase();
  if (raw === 'login') {
    return 'login';
  }
  if (raw === 'api') {
    return 'api';
  }
  const splitLogin =
    process.env.REACT_APP_LOGIN_PROXY_TARGET?.trim() || process.env.LOGIN_PROXY_TARGET?.trim();
  if (splitLogin) {
    return 'login';
  }
  return 'api';
}

/** Allowed POST refresh path tails (no leading/trailing slashes). Never bare `auth`. */
export const AUTH_REFRESH_ALLOWED_PATH_TAILS = [
  'auth/refresh',
  'refreshtokens',
  'refreshtokens/refresh',
] as const;

export type AuthRefreshPathTail = (typeof AUTH_REFRESH_ALLOWED_PATH_TAILS)[number];

const DEFAULT_AUTH_REFRESH_PATH_TAIL: AuthRefreshPathTail = 'refreshtokens/refresh';

/**
 * Path segment(s) after API base for **POST** refresh (no leading slash).
 * Bare `/auth` is rejected — that must never be used as a browser page or refresh URL.
 */
export function resolveAuthRefreshApiPathTail(): string {
  const raw = process.env.REACT_APP_AUTH_REFRESH_PATH?.trim();
  const tail = (raw || DEFAULT_AUTH_REFRESH_PATH_TAIL).replace(/^\/+/, '').replace(/\/+$/, '');
  const normalized = tail.toLowerCase();
  if (
    !(AUTH_REFRESH_ALLOWED_PATH_TAILS as readonly string[]).includes(normalized) ||
    normalized === 'auth'
  ) {
    return DEFAULT_AUTH_REFRESH_PATH_TAIL;
  }
  return normalized;
}

export type AuthRefreshSuccess = {
  accessToken: string;
  /** Present when API rotates refresh credentials — persisted to session storage for subsequent refreshes. */
  rotatedRefreshToken: string | null;
};

/** POST refresh — cookie-only body (`{}`) so httpOnly refreshToken is sent via `credentials: 'include'`. */
export async function postAuthRefreshWithCookie(): Promise<AuthRefreshSuccess> {
  return postAuthRefreshRequest('{}');
}

/** POST refresh — throws on hard failure (for callers that want the error message). */
export async function postAuthRefresh(refreshToken: string): Promise<AuthRefreshSuccess> {
  const rt = refreshToken.trim();
  if (!rt) {
    throw new Error('Refresh token is empty.');
  }
  return postAuthRefreshRequest(JSON.stringify({ refreshToken: rt }));
}

async function postAuthRefreshRequest(body: string | undefined): Promise<AuthRefreshSuccess> {
  const pathTail = resolveAuthRefreshApiPathTail();
  const init: RequestInit = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    cache: 'no-store',
  };
  if (body !== undefined) {
    init.body = body;
  }
  const { response: res } = await fetchWithDevProxyFallback(
    pathTail,
    init,
    resolveAuthRefreshDevProxyRoute(),
  );
  const text = await res.text();
  let parsed: unknown;
  try {
    parsed = JSON.parse(text) as Record<string, unknown>;
  } catch {
    throw new Error(`Refresh response not JSON: ${text.slice(0, 200)}`);
  }
  const obj = parsed as Record<string, unknown>;
  const accessFromJson = extractAccessTokenFromLoginJson(parsed);
  const accessLegacy = obj.accessToken ?? obj.access_token ?? obj.token;
  const access =
    accessFromJson ??
    (typeof accessLegacy === 'string' && accessLegacy.trim() ? accessLegacy.trim() : null);
  const rotated = extractRefreshTokenFromUnknown(parsed);

  if (isSuccessfulHttpStatus(res.status) && access) {
    clearRefreshUnavailable404();
    if (rotated && process.env.REACT_APP_AUTH_REFRESH_DISABLED !== 'true') {
      persistStoredRefreshToken(rotated);
    }
    return { accessToken: access, rotatedRefreshToken: rotated };
  }
  const msg =
    typeof obj.message === 'string' && obj.message.trim()
      ? obj.message.trim()
      : `Refresh failed HTTP ${res.status}: ${JSON.stringify(parsed)}`;
  throw new AuthRefreshHttpError(res.status, msg);
}

/**
 * Single in-flight refresh (same pattern as `accessToken.js` `refreshPromise`).
 * Returns new access JWT or `null` if no refresh token / network / API error.
 */
export function obtainFreshAccessViaRefresh(): Promise<string | null> {
  if (process.env.REACT_APP_AUTH_REFRESH_DISABLED === 'true') {
    return Promise.resolve(null);
  }
  if (isUserInitiatedLogoutGateActive()) {
    return Promise.resolve(null);
  }
  if (!refreshPromise) {
    refreshPromise = (async (): Promise<string | null> => {
      try {
        const { accessToken } = await postAuthRefreshWithCookie();
        return accessToken;
      } catch {
        /* cookie refresh unavailable — try JSON body fallback below */
      }

      const rt = await getBrowserRefreshToken();
      if (!rt?.trim()) {
        return null;
      }
      try {
        const { accessToken } = await postAuthRefresh(rt.trim());
        return accessToken;
      } catch (e) {
        if (
          e instanceof AuthRefreshHttpError &&
          (e.httpStatus === 404 || e.httpStatus === 405 || e.httpStatus === 501)
        ) {
          rememberRefreshUnavailable404();
        }
        return null;
      }
    })().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}
